/**
 * §7.4 出廠出庫。
 * 出廠的量測點 `SHIP-OUT` 是 `forcedMeasured`——強制實秤，不可改為帳面繼承。
 * 後端也會擋（`SHIPMENT_REQUIRES_MEASURED`），這裡先在畫面上說清楚。
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { useMutation, useQuery } from '../api/hooks.js'
import { Alert, Button, Card, Empty, Field, KV, Mono, Select, TextInput } from '../components/ui.js'
import { emptyWeighValue, toWeighPayload, WeighEntry, weighComplete, type WeighValue } from '../components/WeighEntry.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import { count, kgUnit } from '../../../shared/lib/format.js'
import { Link } from '../router.js'

export function Shipment() {
  const { config } = useConfig()
  const dict = useDict()
  const stock = useQuery<{ items: Record<string, unknown>[] }>('/api/inventory/items?limit=300')
  const [itemId, setItemId] = useState('')
  const [buyer, setBuyer] = useState('')
  const [vehicleNo, setVehicleNo] = useState('')
  const [weigh, setWeigh] = useState<WeighValue>(() => emptyWeighValue())
  const [done, setDone] = useState<{ shipmentId: string; shippedKg: number } | null>(null)

  const mp = config?.measurementPoints?.find((m) => m.kind === 'ship_out') ?? null
  const items = (stock.data?.items ?? []).filter((x) => x.status === 'in_stock')
  const item = items.find((x) => x.item_id === itemId)

  const post = useMutation<void, { shipmentId: string; shippedKg: number }>(async (_a, key) =>
    api.post('/api/shipments', {
      buyer, vehicleNo, itemId,
      measurementPointId: mp!.measurementPointId,
      weigh: toWeighPayload(mp!, weigh, []),
    }, { idempotencyKey: key }))

  useEffect(() => { if (!itemId && items[0]) setItemId(items[0].item_id as string) }, [items, itemId])

  if (done) {
    return (
      <Card title="出廠完成" tone="ok">
        <KV items={[
          ['出廠單', <Mono key="a">{done.shipmentId}</Mono>],
          ['出廠重量', kgUnit(done.shippedKg, 1)],
          ['買方', buyer],
          ['車號', vehicleNo],
        ]} />
        <p className="mt-3 text-sm text-ink-600">
          這一批涵蓋的每一片，回收率都會從 0% 變成部分實現——到
          <Link to="/panel" className="mx-1 underline">單片查詢</Link>看得到。
        </p>
        <div className="mt-4 flex gap-2">
          <Button variant="primary" onClick={() => {
            setDone(null); setItemId(''); setBuyer(''); setVehicleNo('')
            setWeigh(emptyWeighValue()); stock.reload()
          }}>再出一批</Button>
          <Link to="/dashboard"><Button>看全鏈概況</Button></Link>
        </div>
      </Card>
    )
  }

  return (
    <Card title="出廠出庫" subtitle={mp ? `量測點 ${mp.measurementPointId} · 強制實秤` : undefined}>
      {items.length === 0 && !stock.loading && <Empty>目前沒有在庫料件可以出廠。</Empty>}

      {items.length > 0 && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="要出的料件" required>
              <Select value={itemId} onChange={(e) => setItemId(e.target.value)}>
                {items.map((x) => (
                  <option key={x.item_id as string} value={x.item_id as string}>
                    {x.item_id as string} · {dict.material(x.material_code as string)} ·
                    {x.net_kg === null ? ' 無重量帳' : ` ${kgUnit(Number(x.net_kg), 1)}`}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="買方" required>
              <TextInput value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="台玻" />
            </Field>
            <Field label="車號" required>
              <TextInput value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="ABC-1234" />
            </Field>
          </div>

          {item && (
            <div className="mt-3 rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
              {dict.material(item.material_code as string)} ·
              {' '}{count(item.panel_count as number)} 片 ·
              帳面 {item.net_kg === null ? '無重量' : kgUnit(Number(item.net_kg), 1)}
            </div>
          )}

          {mp && config && (
            <div className="mt-4">
              <Alert tone="info" title="出廠必須實秤">
                出廠是對外的交付，重量不能用帳面繼承——這個量測點的設定是強制實秤，改不了。
              </Alert>
              <div className="mt-3">
                <WeighEntry
                  mp={mp} config={config} materialCode={(item?.material_code as string) ?? 'ANY'}
                  panels={[]} value={weigh} onChange={setWeigh}
                />
              </div>
            </div>
          )}

          {post.error && <div className="mt-3"><Alert tone="danger" title="出廠失敗">{post.error.message}</Alert></div>}

          <div className="mt-4">
            <Button variant="primary" size="lg"
              disabled={!itemId || !buyer || !vehicleNo || !mp || !weighComplete(mp, weigh, []) || post.pending}
              onClick={async () => { const r = await post.run(); if (r) setDone(r) }}>
              {post.pending ? '過帳中…' : '出廠過帳'}
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
